import { useState, useEffect, useCallback } from "react";

// Hardcoded default locked state: do not rely on process.env or import.meta.env
export const MAINTENANCE_LOCKED = true;

// Storage keys: primary is hp_helix_preview_unlocked, with backward compatibility for hp_helix_alpha_bypass
export const BYPASS_STORAGE_KEY = "hp_helix_preview_unlocked";
export const LEGACY_BYPASS_STORAGE_KEY = "hp_helix_alpha_bypass";
export const DEFAULT_PASSCODE = "helix_alpha_2026";

export interface MaintenanceGateResult {
  isLocked: boolean;
  setIsLocked: React.Dispatch<React.SetStateAction<boolean>>;
  isMaintenanceEnabled: boolean;
  isBypassed: boolean;
  unlock: (passcode: string) => boolean;
  lock: () => void;
}

/**
 * Checks whether maintenance mode is configured/enforced.
 * Hardcoded to true by default; can only be overridden if explicitly disabled.
 */
export function isMaintenanceModeConfigured(): boolean {
  if (typeof import.meta !== "undefined") {
    const env = (import.meta as any).env;
    if (env && (env.VITE_ENABLE_MAINTENANCE_MODE === false || env.VITE_ENABLE_MAINTENANCE_MODE === "false")) {
      return false;
    }
  }
  return MAINTENANCE_LOCKED;
}

/**
 * Returns expected bypass passcode.
 */
export function getExpectedPasscode(): string {
  if (typeof import.meta !== "undefined") {
    const env = (import.meta as any).env;
    if (env?.VITE_MAINTENANCE_PASSCODE) {
      return String(env.VITE_MAINTENANCE_PASSCODE).trim();
    }
  }
  return DEFAULT_PASSCODE;
}

/**
 * Checks if user has bypass in localStorage.
 */
export function hasStoredBypass(): boolean {
  if (typeof window === "undefined" || typeof localStorage === "undefined") return false;
  try {
    const current = localStorage.getItem(BYPASS_STORAGE_KEY);
    const legacy = localStorage.getItem(LEGACY_BYPASS_STORAGE_KEY);
    return current === "true" || legacy === "true";
  } catch {
    return false;
  }
}

/**
 * Checks URL query parameter on initial load:
 * If preview_key=helix_alpha_2026 exists:
 * - Stores localStorage.setItem('hp_helix_preview_unlocked', 'true')
 * - Strips the parameter from the URL using window.history.replaceState
 * - Returns true
 */
export function checkAndCleanUrlBypass(): boolean {
  if (typeof window === "undefined" || !window.location) return false;

  try {
    const searchParams = new URLSearchParams(window.location.search);
    const previewKey = searchParams.get("preview_key") || searchParams.get("bypass_key");
    const expected = getExpectedPasscode();

    if (previewKey && previewKey.trim().toLowerCase() === expected.toLowerCase()) {
      // Store persistent bypass in primary and legacy keys
      localStorage.setItem(BYPASS_STORAGE_KEY, "true");
      localStorage.setItem(LEGACY_BYPASS_STORAGE_KEY, "true");

      // Clean preview_key query param from URL without triggering page reload
      searchParams.delete("preview_key");
      searchParams.delete("bypass_key");
      const cleanSearch = searchParams.toString() ? `?${searchParams.toString()}` : "";
      const cleanUrl = `${window.location.pathname}${cleanSearch}${window.location.hash}`;
      window.history.replaceState(null, "", cleanUrl);
      return true;
    }

    // Also check hash query params if any (e.g., #/?preview_key=...)
    if (window.location.hash && window.location.hash.includes("preview_key=")) {
      const hashParts = window.location.hash.split("?");
      if (hashParts[1]) {
        const hashParams = new URLSearchParams(hashParts[1]);
        const hashKey = hashParams.get("preview_key");
        if (hashKey && hashKey.trim().toLowerCase() === expected.toLowerCase()) {
          localStorage.setItem(BYPASS_STORAGE_KEY, "true");
          localStorage.setItem(LEGACY_BYPASS_STORAGE_KEY, "true");
          hashParams.delete("preview_key");
          const cleanHashParams = hashParams.toString() ? `?${hashParams.toString()}` : "";
          const cleanUrl = `${window.location.pathname}${window.location.search}${hashParts[0]}${cleanHashParams}`;
          window.history.replaceState(null, "", cleanUrl);
          return true;
        }
      }
    }
  } catch (err) {
    console.warn("[useMaintenanceGate] Error inspecting URL bypass:", err);
  }

  return false;
}

/**
 * Hook to manage platform maintenance gatekeeping with developer passkey bypass.
 */
export function useMaintenanceGate(): MaintenanceGateResult {
  const isMaintenanceEnabled = isMaintenanceModeConfigured();

  const [isBypassed, setIsBypassed] = useState<boolean>(() => {
    // 1. Check URL query param first
    const urlBypassed = checkAndCleanUrlBypass();
    if (urlBypassed) return true;

    // 2. Check localStorage
    return hasStoredBypass();
  });

  const [isLocked, setIsLocked] = useState<boolean>(() => {
    const hasBypass = checkAndCleanUrlBypass() || hasStoredBypass();
    return isMaintenanceEnabled && !hasBypass;
  });

  // Keep isLocked in sync if isBypassed or isMaintenanceEnabled change
  useEffect(() => {
    setIsLocked(isMaintenanceEnabled && !isBypassed);
  }, [isMaintenanceEnabled, isBypassed]);

  // Listen for storage changes across tabs or windows
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === BYPASS_STORAGE_KEY || e.key === LEGACY_BYPASS_STORAGE_KEY) {
        const bypassed = e.newValue === "true";
        setIsBypassed(bypassed);
        setIsLocked(isMaintenanceEnabled && !bypassed);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [isMaintenanceEnabled]);

  const unlock = useCallback((passcode: string): boolean => {
    const expected = getExpectedPasscode();
    if (!passcode) return false;

    if (passcode.trim().toLowerCase() === expected.toLowerCase()) {
      try {
        localStorage.setItem(BYPASS_STORAGE_KEY, "true");
        localStorage.setItem(LEGACY_BYPASS_STORAGE_KEY, "true");
      } catch {}
      setIsBypassed(true);
      setIsLocked(false);
      return true;
    }
    return false;
  }, []);

  const lock = useCallback(() => {
    try {
      localStorage.removeItem(BYPASS_STORAGE_KEY);
      localStorage.removeItem(LEGACY_BYPASS_STORAGE_KEY);
    } catch {}
    setIsBypassed(false);
    setIsLocked(isMaintenanceEnabled);
  }, [isMaintenanceEnabled]);

  return {
    isLocked,
    setIsLocked,
    isMaintenanceEnabled,
    isBypassed,
    unlock,
    lock,
  };
}
